/**
 * Supplier Ledger API service
 */
import apiClient from './api';

export interface TransactionType {
  INVOICE: 'invoice';
  PAYMENT: 'payment';
  CREDIT_NOTE: 'credit_note';
  DEBIT_NOTE: 'debit_note';
  OPENING_BALANCE: 'opening_balance';
  ADJUSTMENT: 'adjustment';
}

export interface SupplierLedgerEntry {
  id: string;
  supplier_id: string;
  transaction_date: string;
  transaction_type: keyof TransactionType;
  reference_type: string;
  reference_number: string;
  reference_id: string;
  debit_amount: number;
  credit_amount: number;
  balance: number;
  description?: string;
  created_at: string;
  created_by_admin_id?: string;
}

export interface SupplierLedgerBalanceSummary {
  opening_balance: number;
  total_debits: number;
  total_credits: number;
  closing_balance: number;
}

export interface SupplierLedgerResponse {
  entries: SupplierLedgerEntry[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages?: number;
  balance_summary: SupplierLedgerBalanceSummary;
  supplier_info?: {
    id: string;
    business_name: string;
    contact_person?: string;
    gstin?: string;
    phone: string;
    email?: string;
  };
}

export interface SupplierLedgerSummary {
  supplier_id: string;
  current_balance: number;
  transaction_counts: Record<string, number>;
  latest_transaction_date?: string;
  total_transactions: number;
}

export interface SupplierLedgerFilters {
  start_date?: string;
  end_date?: string;
  transaction_type?: keyof TransactionType;
  page?: number;
  page_size?: number;
}

export interface LedgerExportRequest {
  supplier_id: string;
  start_date?: string;
  end_date?: string;
  transaction_type?: keyof TransactionType;
  format: 'pdf' | 'csv';
  include_summary?: boolean;
}

export interface LedgerExportResponse {
  export_id: string;
  download_url: string;
  expires_at: string;
  format: string;
  total_records: number;
}

export class SupplierLedgerService {
  /**
   * Get supplier ledger entries with filtering and pagination
   */
  static async getSupplierLedger(
    supplierId: string,
    filters?: SupplierLedgerFilters
  ): Promise<SupplierLedgerResponse> {
    const response = await apiClient.get(`/admin/supplier-ledger/${supplierId}`, {
      params: filters
    });
    return response.data;
  }

  /**
   * Get supplier ledger summary information
   */
  static async getSupplierLedgerSummary(supplierId: string): Promise<SupplierLedgerSummary> {
    const response = await apiClient.get(`/admin/supplier-ledger/${supplierId}/summary`);
    return response.data;
  }

  /**
   * Get current balance for a supplier
   */
  static async getSupplierBalance(supplierId: string): Promise<{
    supplier_id: string;
    current_balance: number;
    balance_formatted: string;
  }> {
    const response = await apiClient.get(`/admin/supplier-ledger/${supplierId}/balance`);
    return response.data;
  }

  /**
   * Download supplier ledger as PDF
   */
  static async downloadLedgerPdf(
    supplierId: string,
    filters?: Omit<SupplierLedgerFilters, 'page' | 'page_size'> & {
      include_summary?: boolean;
    }
  ): Promise<Blob> {
    const response = await apiClient.get(`/admin/supplier-ledger/${supplierId}/pdf`, {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Export supplier ledger to PDF or CSV format
   */
  static async exportSupplierLedger(
    supplierId: string,
    exportRequest: LedgerExportRequest
  ): Promise<LedgerExportResponse> {
    const response = await apiClient.post(`/admin/supplier-ledger/${supplierId}/export`, exportRequest);
    return response.data;
  }

  /**
   * Get available transaction types for filtering
   */
  static async getTransactionTypes(): Promise<{
    transaction_types: Array<{ value: string; label: string }>;
  }> {
    // Return the same transaction types as customer ledger
    return {
      transaction_types: [
        { value: 'invoice', label: 'Invoice' },
        { value: 'payment', label: 'Payment' },
        { value: 'credit_note', label: 'Credit Note' },
        { value: 'debit_note', label: 'Debit Note' },
        { value: 'opening_balance', label: 'Opening Balance' },
        { value: 'adjustment', label: 'Adjustment' },
      ]
    };
  }
}
